import React, { useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiLock, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  useEffect(() => {
    document.title = `Your Cart — ${siteConfig.brandName}`;
  }, []);

  const items = cartItems
    .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
    .filter((item) => item.product);
  const subtotal = items.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const remaining = Math.max(0, 200 - subtotal);
  const progress = Math.min(100, (subtotal / 200) * 100);

  if (items.length === 0) {
    return (
      <main className="cart-page cart-page--empty shell">
        <span className="cart-page__icon">0</span>
        <p className="eyebrow">Your selection</p>
        <h1>Your cart is empty</h1>
        <p>Find the pair that will carry you through the day.</p>
        <Link className="btn" to="/shop">Explore the collection</Link>
      </main>
    );
  }

  return (
    <main className="cart-page shell">
      <header className="cart-heading">
        <div><p className="eyebrow">Your selection</p><h1>Cart</h1></div>
        <Link className="link-arrow" to="/shop"><FiArrowLeft /> Continue shopping</Link>
      </header>

      <div className="cart-layout">
        <section className="cart-items" aria-label="Cart items">
          {items.map((item) => (
            <article className="cart-item" key={`${item.id}-${item.size}-${item.color}`}>
              <Link className="cart-item__image" to={`/product/${item.id}`}><img src={item.product.images[0]} alt={item.product.name} /></Link>
              <div className="cart-item__info">
                <p>{item.product.category}</p>
                <h2><Link to={`/product/${item.id}`}>{item.product.name}</Link></h2>
                <span>{item.color}{item.size ? ` · Size ${item.size}` : ""}</span>
                <div className="cart-item__mobile-price">{formatPrice(item.product.price * item.quantity)}</div>
                <div className="cart-quantity">
                  <button type="button" aria-label="Decrease quantity" onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity - 1)}>−</button>
                  <span>{item.quantity}</span>
                  <button type="button" aria-label="Increase quantity" onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)}>+</button>
                </div>
              </div>
              <strong>{formatPrice(item.product.price * item.quantity)}</strong>
              <button
                className="cart-item__remove"
                type="button"
                aria-label={`Remove ${item.product.name}`}
                onClick={() => removeFromCart(item.id, item.size, item.color)}
              ><FiTrash2 /></button>
            </article>
          ))}
        </section>

        <aside className="cart-summary">
          <h2>Order Summary</h2>
          <div className="shipping-progress">
            <p>{remaining > 0 ? <>Spend <b>{formatPrice(remaining)}</b> more for free delivery.</> : <>You qualify for <b>free delivery</b>.</>}</p>
            <span><i style={{ width: `${progress}%` }} /></span>
          </div>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Delivery</dt><dd>Calculated at checkout</dd></div>
            <div className="cart-summary__total"><dt>Total</dt><dd>{formatPrice(subtotal)}</dd></div>
          </dl>
          <button className="btn cart-summary__checkout" type="button" onClick={() => navigate("/checkout")}><FiLock /> Check out</button>
          <p className="cart-summary__note">Secure payment · Visa · Mastercard</p>
          <details>
            <summary>Add an order note</summary>
            <textarea aria-label="Order note" placeholder="Write your message here..." />
          </details>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
